export const fontSnippets = {
	BS: 'font-family: "BS"; font-style: normal; font-weight: 700;',
	roboto: 'font-family: "Roboto", sans-serif; font-style: normal; font-weight: 400;',
	robotoBold: 'font-family: "Roboto", sans-serif; font-style: normal; font-weight: 700;',
} as const;

export const primitiveTypography = {
	fontFamily: { brand: 'BS', sans: 'Roboto, sans-serif' },
	fontWeight: { regular: 400, bold: 700 },
	fontSize: {
		xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem', xl: '2.5rem',
		'2xl': '2.75rem', '3xl': '3rem', '4xl': '4rem', '5xl': '7rem',
	},
	/*
	 * lineHeight — «не меньше, чем нужно письменности».
	 *
	 * Первый аргумент max() — прежняя абсолютная ступень шкалы; второй — минимум,
	 * заданный от кегля самого элемента. `em` внутри custom property считается в
	 * точке использования, поэтому один рубильник на <html> раздаёт каждому пресету
	 * свой минимум, пропорциональный его font-size. По умолчанию переменная не
	 * объявлена → fallback 0px → max() возвращает базу: для всех прежних языков не
	 * меняется ни один пиксель.
	 *
	 * Зачем: у деванагари (hi) content area шрифта = 1.31em (Noto Sans Devanagari,
	 * ascent 0.90 + descent 0.41), чернила худшего случая — 1.231em, тогда как
	 * латиница и кириллица укладываются в 1.17em. На ступенях с ratio 1.14–1.25
	 * (десктопный h1, basicS) матры вылезают за строку и наезжают на соседнюю.
	 *
	 *   --lh-min      — текстовые ступени;
	 *   --lh-min-head — заголовочные: кегль крупный, и общий текстовый минимум
	 *                   разносит заголовок сильнее, чем нужно.
	 *
	 * Значения — в `html[lang="hi"]` (api/resources/css/bx/main.css). Строка целиком
	 * уходит и в CSS-переменную (tokens.css), и в литерал styled-components, поэтому
	 * max()/var() резолвятся одинаково в ванили, в React и внутри Shadow DOM
	 * (кастомные свойства наследуются через границу тени).
	 */
	lineHeight: {
		tight: 'max(2rem, var(--lh-min, 0px))',
		normal: 'max(2.5rem, var(--lh-min, 0px))',
		relaxed: 'max(3.2rem, var(--lh-min, 0px))',
		loose: 'max(4rem, var(--lh-min-head, 0px))',
		xl: 'max(5rem, var(--lh-min-head, 0px))',
		'2xl': 'max(8rem, var(--lh-min-head, 0px))',
	},
} as const;

export const font = {
	basic: `${fontSnippets.roboto} font-size: ${primitiveTypography.fontSize.lg}; line-height: ${primitiveTypography.lineHeight.relaxed};`,
	basicBig: `${fontSnippets.roboto} font-size: ${primitiveTypography.fontSize['xl']}; line-height: ${primitiveTypography.lineHeight.relaxed};`,
	basicS: `${fontSnippets.roboto} font-size: ${primitiveTypography.fontSize.md}; line-height: ${primitiveTypography.lineHeight.normal};`,
	basicBoldS: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize.md}; line-height: ${primitiveTypography.lineHeight.normal};`,
	basicXS: `${fontSnippets.roboto} font-size: ${primitiveTypography.fontSize.sm}; line-height: max(2.625rem, var(--lh-min, 0px));`,
	basicBoldXS: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize.xs}; line-height: ${primitiveTypography.lineHeight.tight}; letter-spacing: calc(0.5px * var(--ls-scale, 1));`,
	accentXL: `${fontSnippets.BS} font-size: ${primitiveTypography.fontSize['4xl']};`,
	accentL: `${fontSnippets.BS} font-size: ${primitiveTypography.fontSize.xl}; line-height: ${primitiveTypography.lineHeight.normal};`,
	accentM: `${fontSnippets.BS} font-size: ${primitiveTypography.fontSize.lg};`,
	accentS: `${fontSnippets.BS} font-size: ${primitiveTypography.fontSize.md}; letter-spacing: calc(0.2px * var(--ls-scale, 1));`,
	upperCase: `${fontSnippets.robotoBold} font-size: 1.25rem; line-height: ${primitiveTypography.lineHeight.tight}; letter-spacing: calc(2px * var(--ls-scale, 1)); text-transform: uppercase;`,
	mobile: {
		h1: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize['4xl']}; line-height: ${primitiveTypography.lineHeight.xl};`,
		h2: `${fontSnippets.robotoBold} font-size: 22px; line-height: ${primitiveTypography.lineHeight.loose};`,
		h3: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize.xl}; line-height: ${primitiveTypography.lineHeight.loose};`,
		h4: `${fontSnippets.robotoBold} font-size: 18px; line-height: max(3.25rem, var(--lh-min-head, 0px));`,
	},
	desktop: {
		h1: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize['5xl']}; line-height: ${primitiveTypography.lineHeight['2xl']};`,
		h2: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize['4xl']}; line-height: max(42px, var(--lh-min-head, 0px));`,
		h3: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize['3xl']}; line-height: ${primitiveTypography.lineHeight.loose};`,
		h4: `${fontSnippets.robotoBold} font-size: ${primitiveTypography.fontSize.xl}; line-height: max(3.25rem, var(--lh-min-head, 0px));`,
	},
	text: {
		captionSmall: `${fontSnippets.BS} font-size: ${primitiveTypography.fontSize.sm};`,
		captionXSmall: `${fontSnippets.BS} font-size: ${primitiveTypography.fontSize.xs}; line-height: ${primitiveTypography.lineHeight.tight}; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;`,
	},
} as const;
